import { Component, OnInit, ViewChild, ComponentFactoryResolver } from '@angular/core';
import { DashboardTypeDirective } from 'src/app/directives/dashboard-type.directive';
import { Dashboard } from 'src/app/entities/dashboard';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { DashboardService } from 'src/app/services/dashboard.service';
import { switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ServiceService } from 'src/app/services/service.service';
import { Service } from 'src/app/entities/service';
import { CsvMappingFields } from 'src/app/entities/csvMappingFields';
import { PortalDashboardGroupService } from 'src/app/services/portalDashboardGroup.service';
import { PortalDashboardGroup } from 'src/app/entities/portalDashboardGroup';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { takenPortalCodes, mintPortalCode } from 'src/app/utils/portal-code-mint';

/** One (service, file format) pair a dashboard charts but the portal does not publish. */
export interface UnpublishedSource {
  serviceId: string;
  serviceName: string;
  fileFormatId: string;
  fileFormatName: string;
}

@Component({
    selector: 'ntr-dashboard-edit',
    templateUrl: './dashboard-edit.component.html',
    styleUrls: ['./dashboard-edit.component.css'],
    standalone: false
})
export class DashboardEditComponent implements OnInit {

  @ViewChild(DashboardTypeDirective, { static: false }) serviceTypeHost: DashboardTypeDirective;
  dashboard: Dashboard = new Dashboard();
  dashboardTypes: Array<any> = [
    { name: 'Multi', tag: 'DASHBOARD.MULTI', key: 'MULTI' },
    { name: 'Stacked', tag: 'DASHBOARD.STACKED', key: 'STACKED' },
    { name: 'Bar', tag: 'DASHBOARD.BAR', key: 'BAR' },
    { name: 'Pie', tag: 'DASHBOARD.PIE', key: 'PIE' }
  ];

  datePeriods: Array<any> = [
    { name: ' --- NONE --- ', tag: 'DASHBOARD.NONE', key: '0' },
    { name: 'Last Month', tag: 'DASHBOARD.LAST_MONTH', key: '1' },
    { name: 'Last 3 Months', tag: 'DASHBOARD.LAST_3_MONTH', key: '3' },
    { name: 'Last 6 Months', tag: 'DASHBOARD.LAST_6_MONTH', key: '6' },
    { name: 'Last Year', tag: 'DASHBOARD.LAST_YEAR', key: '12' },
    { name: 'Last 2 Years', tag: 'DASHBOARD.LAST_2_YEARS', key: '24' }
  ];

  services = [];
  fileFormats = [];
  values = [];
  dates = [];
  allCols = [];
  submitted = false;
  _dashboardType: string;
  _dashboardTypeStr: string;

  serviceId: String;
  serviceName: String;
  fileFormatId: String;
  fileFormatName: String;
  valueCol: String;
  dateCol: String;
  datePeriod: String;
  groupCol1: String;
  groupCol2: String;
  groupCol1Val: String;
  groupCol2Val: String;

  portalGroups: PortalDashboardGroup[] = [];

  @ViewChild('publishModal', { static: false }) publishModal: ModalComponent;
  /** The sources the prompt is currently asking about. Empty whenever it is closed. */
  unpublishedSources: UnpublishedSource[] = [];
  /** The server's refusal from "publish all datasets", shown inside the prompt. */
  publishError: string | null = null;
  publishing = false;

  /**
   * Whether the header should show the dashboard's name rather than "Add
   * Dashboard" — true once a saved dashboard (with an id and name) has
   * loaded. Falls back to "Add Dashboard" while that load is still pending,
   * rather than rendering an empty `Dashboard ""`.
   */
  get isEditingExistingDashboard(): boolean {
    return !!(this.dashboard?.id && this.dashboard?.name);
  }

  constructor(public dashboardService: DashboardService, public serviceService: ServiceService, private router: Router, private route: ActivatedRoute, private componentFactoryResolver: ComponentFactoryResolver, private groupService: PortalDashboardGroupService) {
    dashboardService.dashboardUpdated$.subscribe(
      dashboard => {
        this.dashboard = dashboard;
      });
  }

  /** Group options for the portal dropdown. A failure here must not block editing. */
  loadPortalGroups(): void {
    this.groupService.list().subscribe(
      groups => { this.portalGroups = groups || []; },
      () => { this.portalGroups = []; }
    );
  }

  onSubmit() {
    this.dashboard.type = this._dashboardType;
    if (this.dashboard.periodFilter===false){
      this.dashboard.datePeriod = null
    }
    if (this.dashboard.rangeFilter===false){
      this.dashboard.dateRange = null
    }

    this.unpublishedSources = this.dashboard.isForPortal ? this.findUnpublishedSources() : [];
    if (this.unpublishedSources.length === 0) {
      this.saveDashboard();
      return;
    }
    // Publishing a dashboard over unpublished datasets is legal — the portal
    // renders a dashboard's chart data whether or not its source dataset is in
    // the catalogue — so this is a question, not a validation error.
    this.publishError = null;
    this.publishModal.show();
  }

  /**
   * The dashboard's distinct (service, format) sources that the portal would
   * not publish, in the order the configs name them.
   *
   * A config naming a service or format that no longer exists is skipped
   * rather than reported: there is nothing left to publish, and the dashboard
   * cannot chart it either.
   */
  findUnpublishedSources(): UnpublishedSource[] {
    const out: UnpublishedSource[] = [];
    const seen = new Set<string>();
    (this.dashboard.dashboardConfigs || []).forEach(cfg => {
      const serviceId = String(cfg?.['serviceId'] ?? '');
      const fileFormatId = String(cfg?.['fileFormatId'] ?? '');
      const key = serviceId + '::' + fileFormatId;
      if (!serviceId || !fileFormatId || seen.has(key)) return;
      seen.add(key);

      const svc = this.services.find(s => String(s.id) === serviceId);
      const ff = svc && (svc.fileFormats || []).find((f: any) => String(f?.['id']) === fileFormatId);
      if (!svc || !ff) return;
      if (svc.isForPortal === true && ff['isForPortal'] === true) return;

      out.push({
        serviceId,
        serviceName: svc.name,
        fileFormatId,
        fileFormatName: ff['name'],
      });
    });
    return out;
  }

  /** "Publish only the dashboard": the sources stay unpublished, on purpose. */
  publishDashboardOnly(): void {
    this.publishModal.hide();
    this.saveDashboard();
  }

  /** "Cancel": nothing is saved and nothing is published; the form stays open. */
  cancelPublish(): void {
    this.publishModal.hide();
  }

  /**
   * "Publish all datasets": turn on each affected service's master switch and
   * each named format's own switch, then save the dashboard.
   *
   * Only the formats the dashboard actually charts are published — never a
   * service's other formats, which the admin has not chosen to expose.
   *
   * Each service is re-fetched before being written back: `this.services` was
   * loaded when the form opened and posting that copy would overwrite anything
   * saved on the service in the meantime.
   *
   * The dashboard is saved only once every service save has succeeded. A
   * refused save (a duplicate portal code is a 409) leaves the prompt open
   * with the server's message, because saving the dashboard anyway would
   * publish it over datasets the admin was told would be published and were
   * not.
   */
  publishAllThenSave(): void {
    this.publishError = null;
    this.publishing = true;

    const byService = new Map<string, string[]>();
    this.unpublishedSources.forEach(src => {
      const formats = byService.get(src.serviceId) || [];
      formats.push(src.fileFormatId);
      byService.set(src.serviceId, formats);
    });

    // Seeded once, from every service the form knows about, and added to as
    // codes are minted so two formats published in the same batch cannot be
    // handed the same code.
    const taken = takenPortalCodes(this.services);
    const serviceIds = Array.from(byService.keys());

    forkJoin(serviceIds.map(id => this.serviceService.getService(id))).pipe(
      switchMap((services: Service[]) => forkJoin(services.map((svc, i) => {
        const formatIds = byService.get(serviceIds[i]) || [];
        svc.isForPortal = true;
        (svc.fileFormats || []).forEach((ff: any) => {
          if (!formatIds.includes(String(ff?.['id']))) return;
          ff['isForPortal'] = true;
          ff['portalCode'] = mintPortalCode(ff, svc.code, taken);
          taken.add(String(ff['portalCode']).toLowerCase());
        });
        return this.serviceService.add(svc);
      }))),
    ).subscribe({
      next: () => {
        this.publishing = false;
        this.publishModal.hide();
        this.saveDashboard();
      },
      error: err => {
        this.publishing = false;
        this.publishError = err?.error?.message || err?.message || 'PORTAL.PUBLISH_ALL_FAILED';
      },
    });
  }

  /**
   * `submitted` hides the form (see the template's `[hidden]`), so it is set
   * here — at the point the dashboard is really being saved — and not in
   * onSubmit, which may only be opening the publish prompt. Setting it there
   * would blank the page behind the prompt and leave Cancel with nothing to
   * return to.
   */
  private saveDashboard(): void {
    this.submitted = true;
    this.dashboardService.add(this.dashboard)
      .subscribe(service => {
        this.router.navigateByUrl('/dashboard');
      });
  }

  onChange(dashboardType) {
    this._dashboardType = dashboardType;

  }

  onChangeService(serviceId) {
    this.serviceService.getService(serviceId).subscribe(service => {
      this.serviceName = service.name
      if (service.fileFormats && service.fileFormats.length > 0) {
        this.fileFormats = service.fileFormats
        this.values = [];
        this.dates = [];
        this.allCols = [];
      }
    });
  }

  onChangeFileFormat(fileFormatId) {
    var ff = this.fileFormats.filter(ff => ff.id === fileFormatId);
    this.fileFormatName = ff[0]['name']
    this.values = ff[0]['mappingObjects'].filter(mo => CsvMappingFields.isAmountColumn(mo))
    this.dates = ff[0]['mappingObjects'].filter(mo => CsvMappingFields.isDateColumn(mo))
    this.allCols = ff[0]['mappingObjects']
  }
  dashboardConfigs = []
  addConfig() {

    var dashboardConfigObj = {
      "service": this.serviceName,
      "serviceId": this.serviceId,
      "fileFormat": this.fileFormatName,
      "fileFormatId": this.fileFormatId,
      "valueCol": this.valueCol,
      "dateCol": this.dateCol,
      "groupCol1": this.groupCol1,
      "groupCol2": this.groupCol2,
      "groupCol1Val": this.groupCol1Val,
      "groupCol2Val": this.groupCol2Val
    }
    if (!this.dashboard.dashboardConfigs) {
      this.dashboard.dashboardConfigs = []
    }
    this.dashboard.dashboardConfigs.push(dashboardConfigObj);

  }

  removeDashboardConfig(index) {
    this.dashboard.dashboardConfigs.splice(index, 1);
  }

  goToList() {
    this.router.navigateByUrl('/dashboard');
  }

  ngOnInit(): void {
    this.loadPortalGroups();
    this.dashboard = new Dashboard();
    this.dashboard.rangeFilter = false
    this.dashboard.periodFilter = false
    this.serviceService.getTotalServiceList("").subscribe(services => {
      this.services = services;
      this.route.paramMap.pipe(
        switchMap((params: ParamMap) => this.dashboardService.getDashboard(params.get('id'))))
        .subscribe(dashboard => {
          if (dashboard !== null) {
            this.dashboard = dashboard;
            this.dashboardTypes.forEach((element) => {
              if (element.key === this.dashboard.type) {
                this._dashboardType = element.key;
                this._dashboardTypeStr = this.dashboard.type;
              }
            });
            if (this.dashboard.dateRange)
            this.dashboard.dateRange = [new Date(this.dashboard.dateRange[0]), new Date(this.dashboard.dateRange[1])]
          }
        });
    });
  }


  setDateFilter(event, filterType) {
    if (event.srcElement.checked) {
      if (filterType === 'period') {
        this.dashboard.periodFilter = true
        this.dashboard.rangeFilter = false
      } else {
        this.dashboard.rangeFilter = true
        this.dashboard.periodFilter = false
      }
    } else {
      this.dashboard.rangeFilter = false
      this.dashboard.periodFilter = false
    }
  }
}
